import { Fragment } from 'react';
import PropTypes from 'prop-types';

import { Layout } from '@utilities';

import AddOns from './components/Addons';

import { getCartCost, getDisplayCurrency } from './utilities/helpers';

import styles from './_index.module.scss';

const Cart = ({
  addOns,
  footnote,
  hasCaveats,
  items,
  onAddonChange,
  paymentOption,
  promos,
  totalText = 'Total',
}) => {
  const totalDue = getCartCost({ addOns, currency: paymentOption.currency, items, promos });

  if (!items?.length) {
    return (
      <div className={styles['cart-items']}>
        <p>No items in your cart</p>
      </div>
    );
  }

  return (
    <Layout.Flex.Column as="section" className={styles['cart']}>
      <h3>Standard Costs</h3>
      <Layout.Flex.Column as="ul" className={styles['cart-items']}>
        {items.map((item, index) => {
          const value = item.prices?.[paymentOption.currency] ?? 0;

          return (
            <Fragment key={`cart-item-${index}`}>
              {index > 0 && <hr />}
              <li data-testid="cart-item">
                <Layout.Flex gap="x-large">
                  <Layout.Fill>
                    <h5 className={styles['cart-item-name']}>{item.name}</h5>
                    {item.description && <div>{item.description}</div>}
                  </Layout.Fill>
                  <div className={styles['cart-item-price']}>
                    {`${value} ${getDisplayCurrency({ paymentOption, value })}${hasCaveats ? '*' : ''}`}
                  </div>
                </Layout.Flex>
              </li>
            </Fragment>
          );
        })}
      </Layout.Flex.Column>

      {(addOns?.length > 0 || promos?.length > 0) && (
        <div>
          {addOns?.length > 0 && (
            <AddOns addOns={addOns} onAddonChange={onAddonChange} paymentOption={paymentOption} />
          )}
          {promos?.length > 0 && (
            <ul>
              {promos.map((promo) => (
                <li className={styles['cart-promo']} key={promo.name}>
                  <strong>{promo.name}</strong>
                  <span className={styles['cart-promo-amount']}>
                    -{promo[paymentOption.currency]}{' '}
                    {getDisplayCurrency({
                      paymentOption,
                      value: promo[paymentOption.currency],
                    })}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className={styles['cart-total-row']}>
        <strong>{totalText}</strong>
        <strong>{`${totalDue} ${getDisplayCurrency({ paymentOption, value: totalDue })}${hasCaveats ? '*' : ''}`}</strong>
      </div>
      {footnote && (
        <div aria-label="Additional information" className={styles['cart-footnote']}>
          {footnote}
        </div>
      )}
    </Layout.Flex.Column>
  );
};

Cart.propTypes = {
  addOns: PropTypes.arrayOf(
    PropTypes.shape({
      completes: PropTypes.number,
      credits: PropTypes.number,
      description: PropTypes.string,
      name: PropTypes.string.isRequired,
      selected: PropTypes.bool,
      title: PropTypes.string.isRequired,
    })
  ),
  footnote: PropTypes.node,
  hasCaveats: PropTypes.bool,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.node,
      name: PropTypes.node.isRequired,
      prices: PropTypes.objectOf(PropTypes.number).isRequired,
    })
  ).isRequired,
  onAddonChange: PropTypes.func,
  paymentOption: PropTypes.shape({
    currency: PropTypes.string.isRequired,
    currencySingular: PropTypes.string,
    key: PropTypes.oneOf(['credits', 'credit_card', 'subscription']).isRequired,
  }).isRequired,
  promos: PropTypes.arrayOf(
    PropTypes.shape({
      completes: PropTypes.number,
      credits: PropTypes.number,
      name: PropTypes.string.isRequired,
    })
  ),
  totalText: PropTypes.node,
};

export default Cart;
